/**
 * Get Component Props Tool
 *
 * Provides detailed prop definitions for a specific component.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import componentCatalog from '../data/component-catalog.json' with { type: 'json' };
import type { ComponentCatalog } from '../types.js';

const catalog = componentCatalog as ComponentCatalog;

const GetComponentPropsSchema = z.object({
  componentName: z.string().describe('Name of the component (e.g., "Button", "DashboardLayout")'),
  includeExamples: z.boolean().default(true).describe('Whether to include usage examples'),
});

/**
 * Register the get_component_props tool
 */
export function registerGetComponentPropsTool(server: McpServer): void {
  server.tool(
    'get_component_props',
    'Get detailed prop definitions for a specific component',
    GetComponentPropsSchema.shape,
    async (params) => {
      const { componentName, includeExamples } = GetComponentPropsSchema.parse(params);

      const component = catalog.components.find(
        (c) => c.name.toLowerCase() === componentName.toLowerCase()
      );

      if (!component) {
        // Suggest similar components
        const suggestions = catalog.components
          .filter((c) => c.name.toLowerCase().includes(componentName.toLowerCase().slice(0, 3)))
          .slice(0, 5)
          .map((c) => c.name);

        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify(
                {
                  error: `Component "${componentName}" not found`,
                  suggestions: suggestions.length > 0 ? suggestions : undefined,
                  hint: 'Use search_components tool to find available components',
                },
                null,
                2
              ),
            },
          ],
        };
      }

      const result: Record<string, unknown> = {
        name: component.name,
        description: component.description,
        category: component.category,
        importPath: component.importPath,
        props: component.props,
        variants: component.variants,
        slots: component.slots,
        events: component.events,
        accessibility: component.accessibility,
        relatedComponents: component.relatedComponents,
      };

      if (includeExamples) {
        result.examples = component.examples;
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(result, null, 2),
          },
        ],
      };
    }
  );
}
